/* Ticket Sales tab — paid Stripe orders (NYE, Vivid, Aus Day, Boxing Day, etc.).
   Click a row to expand and preview the boarding pass PDF inline.
   Use the Edit button to fix customer name/email/phone.
   Use the Resend button to re-fire the confirmation email + fresh boarding pass. */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ShoppingBag, RefreshCcw, Download, Eye, X, Pencil, Send, Loader2, Archive, ArchiveRestore } from "lucide-react";
import { adminApi, adminDownloadPdf } from "./adminApi";

const PRODUCT_LABEL = {
 "vivid-2027": "Vivid 2027",
 "nye-2026": "NYE 2026",
 "event-2026": "Event",
};

export default function AdminOrders({ onUnauthorized, showToast }) {
 const [items, setItems] = useState([]);
 const [loading, setLoading] = useState(true);
 const [openSession, setOpenSession] = useState(null);
 const [editTarget, setEditTarget] = useState(null); // order being edited
 const [editForm, setEditForm] = useState({ customer_name: "", customer_email: "", customer_phone: "" });
 const [savingEdit, setSavingEdit] = useState(false);
 const [resendingSession, setResendingSession] = useState(null);
 const [view, setView] = useState("active"); // "active" | "archived"
 const [selected, setSelected] = useState(new Set());
 const [busy, setBusy] = useState(false);

 const load = useCallback(async () => {
  setLoading(true);
  setSelected(new Set());
  try {
   const params = view === "archived" ? "?limit=500&archived_only=true" : "?limit=500";
   const res = await adminApi(`/admin/orders${params}`);
   setItems(res.items || []);
  } catch (e) {
   if (e.status === 401) { onUnauthorized(); return; }
   showToast(e.message || "Failed to load orders", "err");
  } finally { setLoading(false); }
 }, [onUnauthorized, showToast, view]);

 useEffect(() => { load(); }, [load]);

 const totalRevenue = items.reduce((sum, o) => sum + (o.amount || 0), 0);
 const togglePreview = (sid) => setOpenSession((p) => p === sid ? null : sid);

 const openEdit = (o) => {
  const meta = o.metadata || {};
  setEditTarget(o);
  setEditForm({
   customer_name: meta.customer_name || "",
   customer_email: meta.customer_email || "",
   customer_phone: meta.customer_phone || "",
  });
 };

 const closeEdit = () => { setEditTarget(null); setSavingEdit(false); };

 const saveEdit = async () => {
  if (!editTarget?.session_id) return;
  setSavingEdit(true);
  try {
   await adminApi(`/admin/orders/${editTarget.session_id}`, {
    method: "PUT", body: editForm,
   });
   showToast("Order updated. Click Resend to re-send the confirmation.", "ok");
   closeEdit();
   load();
  } catch (e) {
   if (e.status === 401) { onUnauthorized(); return; }
   showToast(e.message || "Update failed", "err");
   setSavingEdit(false);
  }
 };

 const resend = async (o) => {
  if (!o.session_id) return;
  const meta = o.metadata || {};
  const target = meta.customer_email || "";
  if (!target) {
   showToast("Customer email missing on this order. Edit it first.", "err");
   return;
  }
  if (!window.confirm(`Re-send confirmation email to ${target}?`)) return;
  setResendingSession(o.session_id);
  try {
   const res = await adminApi(`/admin/orders/${o.session_id}/resend`, { method: "POST" });
   showToast(`Confirmation re-sent to ${res.sent_to || target}.`, "ok");
  } catch (e) {
   if (e.status === 401) { onUnauthorized(); return; }
   showToast(e.message || "Resend failed", "err");
  } finally { setResendingSession(null); }
 };

 const archiveOne = async (o, archived = true) => {
  if (!o.session_id) return;
  setBusy(true);
  try {
   await adminApi(`/admin/orders/${o.session_id}/archive`, {
    method: "PATCH", body: { archived },
   });
   showToast(archived ? "Order archived." : "Order restored.", "ok");
   load();
  } catch (e) {
   if (e.status === 401) { onUnauthorized(); return; }
   showToast(e.message || "Archive failed", "err");
  } finally { setBusy(false); }
 };

 const toggleSelect = (sid) => {
  setSelected((cur) => {
   const next = new Set(cur);
   if (next.has(sid)) next.delete(sid); else next.add(sid);
   return next;
  });
 };

 const toggleSelectAll = () => {
  const visibleIds = items.map((o) => o.session_id).filter(Boolean);
  setSelected((cur) => {
   const allSelected = visibleIds.length > 0 && visibleIds.every((id) => cur.has(id));
   if (allSelected) {
    const next = new Set(cur);
    visibleIds.forEach((id) => next.delete(id));
    return next;
   }
   const next = new Set(cur);
   visibleIds.forEach((id) => next.add(id));
   return next;
  });
 };

 const bulkArchive = async (archived = true) => {
  const session_ids = [...selected];
  if (!session_ids.length) return;
  setBusy(true);
  try {
   const res = await adminApi(`/admin/orders/bulk-archive`, {
    method: "POST", body: { session_ids, archived },
   });
   showToast(`${archived ? "Archived" : "Restored"} ${res.modified} order${res.modified === 1 ? "" : "s"}.`, "ok");
   load();
  } catch (e) {
   if (e.status === 401) { onUnauthorized(); return; }
   showToast(e.message || "Bulk action failed", "err");
  } finally { setBusy(false); }
 };

 const viewingArchived = view === "archived";
 const visibleIds = items.map((o) => o.session_id).filter(Boolean);
 const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
 const someSelected = selected.size > 0;

 return (
  <div data-testid="admin-tab-orders">
   <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
    <div>
     <div className="flex items-center gap-2 text-amber-300 text-[10px] uppercase tracking-[0.32em] font-black mb-2">
      <ShoppingBag className="w-3.5 h-3.5" /> Ticket Sales &middot; {items.length}
     </div>
     <p className="text-white/65 text-sm max-w-2xl">Paid orders from every Stripe-driven ticket sale (NYE, Vivid, Australia Day, Boxing Day). Edit customer details or re-send the boarding pass email at any time.</p>
    </div>
    <div className="flex items-center gap-4">
     <div className="text-right">
      <div className="text-[10px] uppercase tracking-[0.28em] font-black text-white/45">Gross revenue</div>
      <div className="text-amber-300 font-black text-xl tabular-nums">${totalRevenue.toLocaleString("en-AU", { minimumFractionDigits: 2 })}</div>
     </div>
     <button onClick={load} className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-white/15 hover:bg-white/[0.06] text-white/75 text-[10px] uppercase tracking-[0.22em] font-bold transition-colors" data-testid="admin-orders-refresh"><RefreshCcw className="w-3 h-3" /> Refresh</button>
     <button
      onClick={async () => {
       try {
        const params = viewingArchived ? "?archived_only=true" : "";
        await adminDownloadPdf(`/admin/orders/export/pdf${params}`);
        showToast(`Exported ${items.length} order${items.length === 1 ? "" : "s"} as PDF.`);
       } catch (e) {
        if (e.status === 401) { onUnauthorized(); return; }
        showToast(e.message || "PDF export failed", "err");
       }
      }}
      disabled={!items.length}
      className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-gradient-to-r from-amber-300 to-amber-500 text-[#0A0A0A] text-[10px] uppercase tracking-[0.22em] font-black disabled:opacity-30 disabled:cursor-not-allowed hover:scale-[1.02] transition-transform"
      data-testid="admin-orders-pdf">
      <Download className="w-3 h-3" /> Export PDF
     </button>
    </div>
   </div>

   {/* View filter pills */}
   <div className="flex flex-wrap gap-2 mb-4" data-testid="admin-orders-filters">
    <button onClick={() => setView("active")} className={`px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.22em] font-black transition-colors inline-flex items-center gap-1.5 ${view === "active" ? "bg-amber-400 text-[#0A0A0A]" : "border border-white/15 text-white/70 hover:bg-white/[0.06]"}`} data-testid="admin-orders-filter-active">
     Active
    </button>
    <button onClick={() => setView("archived")} className={`px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.22em] font-black transition-colors inline-flex items-center gap-1.5 ${view === "archived" ? "bg-amber-400 text-[#0A0A0A]" : "border border-white/15 text-white/55 hover:bg-white/[0.06]"}`} data-testid="admin-orders-filter-archived">
     <Archive className="w-3 h-3" /> Archived
    </button>
   </div>

   {/* Bulk action bar */}
   {someSelected && (
    <div className="mb-4 flex items-center gap-2 flex-wrap rounded-2xl border border-amber-300/30 bg-amber-300/[0.06] px-4 py-3" data-testid="admin-orders-bulkbar">
     <span className="text-amber-200 text-xs font-bold uppercase tracking-[0.2em]">{selected.size} selected</span>
     <div className="flex-1" />
     <button
      onClick={async () => {
       const sids = [...selected];
       if (!sids.length) return;
       try {
        await adminDownloadPdf(`/admin/orders/export/pdf?session_ids=${encodeURIComponent(sids.join(","))}`);
        showToast(`Exported ${sids.length} selected order${sids.length === 1 ? "" : "s"} as PDF.`, "ok");
       } catch (e) {
        if (e.status === 401) { onUnauthorized(); return; }
        showToast(e.message || "PDF export failed", "err");
       }
      }}
      disabled={busy}
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.06] hover:bg-white/[0.12] text-white/85 text-[10px] uppercase tracking-[0.18em] font-bold disabled:opacity-40"
      data-testid="admin-orders-bulk-pdf"
     ><Download className="w-3 h-3" /> Export PDF</button>
     {viewingArchived ? (
      <button onClick={() => bulkArchive(false)} disabled={busy} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-400/15 hover:bg-emerald-400/25 text-emerald-200 text-[10px] uppercase tracking-[0.18em] font-bold disabled:opacity-40" data-testid="admin-orders-bulk-restore"><ArchiveRestore className="w-3 h-3" /> Restore</button>
     ) : (
      <button onClick={() => bulkArchive(true)} disabled={busy} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-400/15 hover:bg-sky-400/25 text-sky-200 text-[10px] uppercase tracking-[0.18em] font-bold disabled:opacity-40" data-testid="admin-orders-bulk-archive"><Archive className="w-3 h-3" /> Archive</button>
     )}
     <button onClick={() => setSelected(new Set())} className="text-white/55 hover:text-white text-[10px] uppercase tracking-[0.18em] font-bold ml-1" data-testid="admin-orders-bulk-clear">Clear</button>
    </div>
   )}

   {loading ? (
    <div className="text-white/45 text-sm py-12 text-center">Loading…</div>
   ) : items.length === 0 ? (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] py-16 text-center">
     <div className="text-white/40 text-sm">{viewingArchived ? "No archived orders." : "No paid orders yet."}</div>
    </div>
   ) : (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-x-auto">
     <table className="w-full text-sm min-w-[920px]">
      <thead>
       <tr className="border-b border-white/10 text-[10px] uppercase tracking-[0.22em] font-black text-white/45">
        <th className="text-left px-4 py-3 w-10">
         <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll} className="w-4 h-4 rounded accent-amber-400 cursor-pointer" data-testid="admin-orders-selectall" />
        </th>
        <th className="text-left px-5 py-3">Paid at</th>
        <th className="text-left px-5 py-3">Customer</th>
        <th className="text-left px-5 py-3">Product</th>
        <th className="text-left px-5 py-3">Tickets</th>
        <th className="text-right px-5 py-3">Amount</th>
        <th className="text-right px-5 py-3">Actions</th>
       </tr>
      </thead>
      <tbody>
       {items.map((o, idx) => {
        const meta = o.metadata || {};
        const product = meta.product;
        const productLabel = PRODUCT_LABEL[product] || product || "Booking";
        const adults = parseInt(meta.adult_qty || "0", 10);
        const children = parseInt(meta.child_qty || "0", 10);
        const paidAt = o.paid_at ? new Date(o.paid_at).toLocaleString("en-AU") : "";
        const isOpen = openSession && openSession === o.session_id;
        const pdfHref = o.session_id ? `${process.env.REACT_APP_BACKEND_URL}/api/tickets/${o.session_id}/pdf` : null;
        const isResending = resendingSession === o.session_id;
        const isSelected = o.session_id && selected.has(o.session_id);
        return (
         <React.Fragment key={`${o.session_id || o.id || "row"}-${idx}`}>
         <tr
          onClick={() => o.session_id && togglePreview(o.session_id)}
          className={`border-b border-white/[0.06] hover:bg-white/[0.02] ${o.session_id ? "cursor-pointer" : ""} ${isOpen ? "bg-white/[0.04]" : ""} ${isSelected ? "bg-amber-300/[0.05]" : ""}`}
          data-testid={`admin-order-row-${idx}`}>
          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
           <input
            type="checkbox"
            checked={!!isSelected}
            onChange={() => o.session_id && toggleSelect(o.session_id)}
            className="w-4 h-4 rounded accent-amber-400 cursor-pointer"
            data-testid={`admin-order-row-${idx}-checkbox`}
           />
          </td>
          <td className="px-5 py-3 text-white/70 whitespace-nowrap">
           {paidAt}
           {o.archived && <span className="ml-2 px-1.5 py-0.5 rounded-full bg-white/[0.08] text-white/55 text-[9px] uppercase tracking-wider font-black">Archived</span>}
          </td>
          <td className="px-5 py-3">
           <div className="text-white font-bold">{meta.customer_name || "(unknown)"}</div>
           <div className="text-white/45 text-xs">{meta.customer_email || ""}</div>
           {meta.customer_phone && <div className="text-white/35 text-xs">{meta.customer_phone}</div>}
          </td>
          <td className="px-5 py-3">
           <div className="text-amber-200 font-bold">{productLabel}</div>
           <div className="text-white/45 text-xs">
            {meta.event_name || meta.tier_name || ""}
            {meta.date ? ` · ${meta.date}` : ""}
            {meta.time ? ` · ${meta.time}` : ""}
           </div>
          </td>
          <td className="px-5 py-3 text-white/85 tabular-nums">
           {adults > 0 && `${adults} adult${adults > 1 ? "s" : ""}`}
           {adults > 0 && children > 0 && " · "}
           {children > 0 && `${children} child${children > 1 ? "ren" : ""}`}
          </td>
          <td className="px-5 py-3 text-right text-white font-black tabular-nums">${Number(o.amount || 0).toLocaleString("en-AU", { minimumFractionDigits: 2 })}</td>
          <td className="px-5 py-3 text-right whitespace-nowrap">
           <button
            onClick={(e) => { e.stopPropagation(); openEdit(o); }}
            className="inline-flex items-center gap-1 text-white/65 hover:text-amber-300 mr-3"
            data-testid={`admin-order-${idx}-edit`}
            title="Edit customer details">
            <Pencil className="w-3.5 h-3.5" />
           </button>
           <button
            onClick={(e) => { e.stopPropagation(); resend(o); }}
            disabled={isResending}
            className="inline-flex items-center gap-1 text-white/65 hover:text-amber-300 mr-3 disabled:opacity-40"
            data-testid={`admin-order-${idx}-resend`}
            title="Re-send confirmation email">
            {isResending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
           </button>
           {o.archived ? (
            <button
             onClick={(e) => { e.stopPropagation(); archiveOne(o, false); }}
             disabled={busy}
             className="inline-flex items-center gap-1 text-emerald-300 hover:text-emerald-200 mr-3 disabled:opacity-40"
             data-testid={`admin-order-${idx}-restore`}
             title="Restore from archive">
             <ArchiveRestore className="w-3.5 h-3.5" />
            </button>
           ) : (
            <button
             onClick={(e) => { e.stopPropagation(); archiveOne(o, true); }}
             disabled={busy}
             className="inline-flex items-center gap-1 text-sky-300 hover:text-sky-200 mr-3 disabled:opacity-40"
             data-testid={`admin-order-${idx}-archive`}
             title="Archive (hide from active view)">
             <Archive className="w-3.5 h-3.5" />
            </button>
           )}
           {pdfHref && (
            <>
             <button
              onClick={(e) => { e.stopPropagation(); togglePreview(o.session_id); }}
              className="inline-flex items-center gap-1 text-white/65 hover:text-amber-300 mr-2"
              data-testid={`admin-order-${idx}-preview`}
              title="Preview boarding pass">
              <Eye className="w-3.5 h-3.5" />
             </button>
             <a
              href={pdfHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 text-amber-300 hover:text-amber-200"
              data-testid={`admin-order-${idx}-pdf`}
              title="Open PDF">
              <Download className="w-3.5 h-3.5" />
             </a>
            </>
           )}
          </td>
         </tr>
         {isOpen && pdfHref && (
          <tr className="border-b border-white/[0.06]" data-testid={`admin-order-${idx}-preview-row`}>
           <td colSpan={7} className="p-0 bg-[#0F1525]">
            <div className="p-4 lg:p-6">
             <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.28em] font-black text-white/55">Boarding pass preview</div>
              <button onClick={() => setOpenSession(null)} className="inline-flex items-center gap-1 text-white/55 hover:text-white text-[10px] uppercase tracking-[0.22em] font-bold" data-testid={`admin-order-${idx}-close-preview`}><X className="w-3 h-3" /> Close</button>
             </div>
             <div className="rounded-2xl overflow-hidden border border-white/10 bg-white">
              <iframe
               title={`Boarding pass preview ${idx}`}
               src={`${pdfHref}#toolbar=0&navpanes=0`}
               className="w-full"
               style={{ height: "720px", border: "none" }}
              />
             </div>
             <div className="mt-3 flex items-center gap-3 text-[10px] uppercase tracking-[0.22em] font-bold text-white/45">
              <span>Session · {o.session_id?.slice(0, 20)}{o.session_id && o.session_id.length > 20 ? "…" : ""}</span>
              <span>·</span>
              <a href={pdfHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-amber-300 hover:text-amber-200"><Download className="w-3 h-3" /> Open / Download</a>
             </div>
            </div>
           </td>
          </tr>
         )}
         </React.Fragment>
        );
       })}
      </tbody>
     </table>
    </div>
   )}

   {/* ── EDIT MODAL ── */}
   {editTarget && (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm px-4" data-testid="admin-order-edit-modal" onClick={closeEdit}>
     <div className="w-full max-w-md rounded-2xl bg-[#0F1525] ring-1 ring-white/15 p-7 shadow-2xl" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-5">
       <div>
        <div className="text-[10px] uppercase tracking-[0.28em] font-black text-amber-300">Edit booking</div>
        <div className="text-white font-bold text-lg">Ref {(editTarget.session_id || "").slice(-8).toUpperCase()}</div>
        <div className="text-white/55 text-xs mt-1">
         {(editTarget.metadata?.customer_name) || "(unknown)"}
         {editTarget.metadata?.product ? ` · ${PRODUCT_LABEL[editTarget.metadata.product] || editTarget.metadata.product}` : ""}
         {editTarget.metadata?.date ? ` · ${editTarget.metadata.date}` : ""}
        </div>
       </div>
       <button onClick={closeEdit} className="text-white/55 hover:text-white" data-testid="admin-order-edit-close"><X className="w-5 h-5" /></button>
      </div>

      <div className="space-y-4">
       <div>
        <label className="block text-[10px] uppercase tracking-[0.22em] font-bold text-white/55 mb-1.5">Customer name</label>
        <input
         type="text"
         value={editForm.customer_name}
         onChange={(e) => setEditForm({ ...editForm, customer_name: e.target.value })}
         className="w-full rounded-xl bg-white/5 ring-1 ring-white/10 focus:ring-amber-300/60 outline-none px-4 py-2.5 text-white placeholder-white/30"
         data-testid="admin-order-edit-name"
        />
       </div>
       <div>
        <label className="block text-[10px] uppercase tracking-[0.22em] font-bold text-white/55 mb-1.5">Customer email</label>
        <input
         type="email"
         value={editForm.customer_email}
         onChange={(e) => setEditForm({ ...editForm, customer_email: e.target.value })}
         className="w-full rounded-xl bg-white/5 ring-1 ring-white/10 focus:ring-amber-300/60 outline-none px-4 py-2.5 text-white placeholder-white/30"
         data-testid="admin-order-edit-email"
        />
       </div>
       <div>
        <label className="block text-[10px] uppercase tracking-[0.22em] font-bold text-white/55 mb-1.5">Customer phone</label>
        <input
         type="tel"
         value={editForm.customer_phone}
         onChange={(e) => setEditForm({ ...editForm, customer_phone: e.target.value })}
         className="w-full rounded-xl bg-white/5 ring-1 ring-white/10 focus:ring-amber-300/60 outline-none px-4 py-2.5 text-white placeholder-white/30"
         data-testid="admin-order-edit-phone"
        />
       </div>
      </div>

      <div className="mt-7 flex gap-2.5">
       <button
        onClick={saveEdit}
        disabled={savingEdit}
        className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-amber-300 hover:bg-amber-200 text-[#0A1E3D] font-black uppercase tracking-[0.18em] text-[11px] disabled:opacity-60"
        data-testid="admin-order-edit-save"
       >
        {savingEdit ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Save changes
       </button>
       <button
        onClick={closeEdit}
        className="px-5 py-3 rounded-full bg-white/5 hover:bg-white/10 text-white/75 font-bold uppercase tracking-[0.18em] text-[11px] ring-1 ring-white/10"
        data-testid="admin-order-edit-cancel"
       >
        Cancel
       </button>
      </div>
      <p className="mt-4 text-[11px] text-white/45 leading-relaxed">
       Tip: after saving, click the <span className="text-amber-300">Send</span> icon on the row to re-fire the confirmation email with the updated details.
      </p>
     </div>
    </div>
   )}
  </div>
 );
}
